import { useCallback, useEffect, useRef, useState } from "react";
import { fetchSmsCreditBalance } from "Services/CommonServices";
import { SmsCreditBalance } from "types/smsCredit";
import { getApiStatus } from "utils/apiStatus";

export interface SmsCreditState {
  balance: number;
  /** Full response, or null while loading or when the account has no SMS. */
  credits: SmsCreditBalance | null;
  isLoading: boolean;
  /**
   * The plan has no SMS at all (`403`) — the caller hides every SMS credit
   * affordance rather than offering an upgrade path here.
   */
  isUnavailable: boolean;
  /**
   * The read failed for some other reason (network, 5xx, an endpoint that
   * isn't deployed yet). Distinct from a genuine zero balance: without it a
   * failed call renders as a real empty wallet with the buy control silently
   * hidden, which is indistinguishable from "nothing is on sale" and hides the
   * actual problem from whoever is looking at it.
   */
  hasFailed: boolean;
  refresh: () => Promise<SmsCreditBalance | null>;
  /**
   * Re-read the balance until it moves off `from`, for the gap after a 3-D
   * Secure approval — the credits land with Stripe's notification to the
   * backend, which is near-instant but not synchronous with the purchase call.
   * Resolves with the new balance, or `null` if it hadn't moved in time.
   */
  waitForBalanceChange: (from: number) => Promise<number | null>;
}

/** Poll cadence and ceiling for the post-3DS wait, ~10s in total. */
const POLL_INTERVAL_MS = 1200;
const POLL_ATTEMPTS = 8;

/**
 * The account's prepaid SMS wallet.
 *
 * Every failure other than `403` leaves the balance at zero and simply means
 * there is nothing to show — the send flow falls back to the server's own
 * check, which is the authority either way. `403` is different: it says the
 * plan doesn't include SMS, and the caller must hide the feature.
 */
export const useSmsCredits = (enabled: boolean = true): SmsCreditState => {
  const [credits, setCredits] = useState<SmsCreditBalance | null>(null);
  const [isLoading, setLoading] = useState<boolean>(enabled);
  const [isUnavailable, setUnavailable] = useState<boolean>(false);
  const [hasFailed, setFailed] = useState<boolean>(false);
  // Guards a resolved promise from writing state after the caller unmounted.
  const mounted = useRef<boolean>(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async (): Promise<SmsCreditBalance | null> => {
    if (!enabled) {
      setCredits(null);
      setFailed(false);
      setLoading(false);
      return null;
    }
    try {
      setLoading(true);
      const response = await fetchSmsCreditBalance();
      if (!mounted.current) return response ?? null;
      setCredits(response ?? null);
      setUnavailable(false);
      setFailed(false);
      return response ?? null;
    } catch (error: any) {
      if (!mounted.current) return null;
      const notInPlan = getApiStatus(error) === 403;
      setCredits(null);
      setUnavailable(notInPlan);
      setFailed(!notInPlan);
      return null;
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  const waitForBalanceChange = useCallback(
    async (from: number): Promise<number | null> => {
      for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (!mounted.current) return null;
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetchSmsCreditBalance();
          if (mounted.current) setCredits(response ?? null);
          if (response && response.balance !== from) return response.balance;
        } catch {
          // A failed poll is not a failed purchase — keep waiting.
        }
      }
      return null;
    },
    [],
  );

  return {
    balance: credits?.balance ?? 0,
    credits,
    isLoading,
    isUnavailable,
    hasFailed,
    refresh: load,
    waitForBalanceChange,
  };
};

export default useSmsCredits;
